import { useEffect, useRef } from "react";

var edgeSize = 100;

// I adjust the window scrolling in response to the given mousemove event.
export function handleAutoScroll( event, timerRef, handleMoveItem ) {
    let scrollingContainer;

    // If event.target is inside an element with data-autoscroll
    // then we should scroll the window.
    if (event.target.closest('[data-autoscroll]')) {
        scrollingContainer = event.target.closest('[data-autoscroll]');
    }

 
    // NOTE: Much of the information here, with regard to document dimensions,
    // viewport dimensions, and window scrolling is derived from JavaScript.info.
    // I am consuming it here primarily as NOTE TO SELF.
    // --
    // Read More: https://javascript.info/size-and-scroll-window
    // --
    // CAUTION: The viewport and document dimensions can all be CACHED and then
    // recalculated on window-resize events (for the most part). I am keeping it
    // all here in the mousemove event handler to remove as many of the moving
    // parts as possible and keep the demo as simple as possible.

    // Get the viewport-relative coordinates of the mousemove event.
    let viewportX = event.clientX;
    let viewportY = event.clientY;

    // Get the viewport dimensions.
    let viewportWidth = document.documentElement.clientWidth;
    let viewportHeight = document.documentElement.clientHeight;

    if (scrollingContainer) {
        // If there's a scrolling container, adjust the "viewport"

        // Get mouse position relative to viewport
        viewportX = event.clientX + window.scrollX - scrollingContainer.offsetLeft;
        viewportY = event.clientY + window.scrollY + - scrollingContainer.offsetTop;

        viewportWidth = scrollingContainer.clientWidth;
        viewportHeight = scrollingContainer.clientHeight;
    }

    // Next, we need to determine if the mouse is within the "edge" of the
    // viewport, which may require scrolling the window. To do this, we need to
    // calculate the boundaries of the edge in the viewport (these coordinates
    // are relative to the viewport grid system).
    let edgeTop = edgeSize;
    let edgeLeft = edgeSize;
    let edgeBottom = ( viewportHeight - edgeSize );
    let edgeRight = ( viewportWidth - edgeSize );

    let isInLeftEdge = ( viewportX < edgeLeft );
    let isInRightEdge = ( viewportX > edgeRight );
    let isInTopEdge = ( viewportY < edgeTop );
    let isInBottomEdge = ( viewportY > edgeBottom );

    // If the mouse is not in the viewport edge, there's no need to calculate
    // anything else.
    if ( ! ( isInLeftEdge || isInRightEdge || isInTopEdge || isInBottomEdge ) ) {
        clearTimeout( timerRef.current );
        return;
    }

    // If we made it this far, the user's mouse is located within the edge of the
    // viewport. As such, we need to check to see if scrolling needs to be done.

    // Get the document dimensions.
    // --
    // NOTE: The various property reads here are for cross-browser compatibility
    // as outlined in the JavaScript.info site (link provided above).
    let scrollWidth = Math.max(
        document.body.scrollWidth,
        document.body.offsetWidth,
        document.body.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth,
        document.documentElement.clientWidth
    );
    let scrollHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.body.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
        document.documentElement.clientHeight
    );

    if (scrollingContainer) {
        scrollWidth = scrollingContainer.scrollWidth;
        scrollHeight = scrollingContainer.scrollHeight;
    }

    // Calculate the maximum scroll offset in each direction. Since you can only
    // scroll the overflow portion of the document, the maximum represents the
    // length of the document that is NOT in the viewport.
    var maxScrollX = ( scrollWidth - viewportWidth );
    var maxScrollY = ( scrollHeight - viewportHeight );

    // As we examine the mousemove event, we want to adjust the window scroll in
    // immediate response to the event; but, we also want to continue adjusting
    // the window scroll if the user rests their mouse in the edge boundary. To
    // do this, we'll invoke the adjustment logic immediately. Then, we'll setup
    // a timer that continues to invoke the adjustment logic while the window can
    // still be scrolled in a particular direction.
    // --
    // NOTE: There are probably better ways to handle the ongoing animation
    // check. But, the point of this demo is really about the math logic, not so
    // much about the interval logic.
    (function checkForWindowScroll() {
        clearTimeout( timerRef.current );

        if ( adjustWindowScroll() ) {
            timerRef.current = setTimeout( checkForWindowScroll, 10 );
        }
    })();

    // Adjust the window scroll based on the user's mouse position. Returns True
    // or False depending on whether or not the window scroll was changed.
    function adjustWindowScroll() {

        // Get the current scroll position of the document.
        var currentScrollX = window.pageXOffset;
        var currentScrollY = window.pageYOffset;

        if (scrollingContainer) {
            currentScrollX = scrollingContainer.scrollLeft;
            currentScrollY = scrollingContainer.scrollTop;
        }

        // Determine if the window can be scrolled in any particular direction.
        var canScrollUp = ( currentScrollY > 0 );
        var canScrollDown = ( currentScrollY < maxScrollY );
        var canScrollLeft = ( currentScrollX > 0 );
        var canScrollRight = ( currentScrollX < maxScrollX );

        // Since we can potentially scroll in two directions at the same time,
        // let's keep track of the next scroll, starting with the current scroll.
        // Each of these values can then be adjusted independently in the logic
        // below.
        var nextScrollX = currentScrollX;
        var nextScrollY = currentScrollY;

        // As we examine the mouse position within the edge, we want to make the
        // incremental scroll changes more "intense" the closer that the user
        // gets the viewport edge. As such, we'll calculate the percentage that
        // the user has made it "through the edge" when calculating the delta.
        // Then, that use that percentage to back-off from the "max" step value.
        var maxStep = 50;
        let intensity;

        // Should we scroll left?
        if ( isInLeftEdge && canScrollLeft ) {
            intensity = (edgeLeft - viewportX) / edgeSize;
            nextScrollX = nextScrollX - (maxStep * intensity);

        // Should we scroll right?
        } else if ( isInRightEdge && canScrollRight ) {
            intensity = (viewportX - edgeRight) / edgeSize;
            nextScrollX = nextScrollX + (maxStep * intensity);

        }

        // Should we scroll up?
        if ( isInTopEdge && canScrollUp ) {
            intensity = (edgeTop - viewportY) / edgeSize;
            nextScrollY = nextScrollY - (maxStep * intensity);

        // Should we scroll down?
        } else if ( isInBottomEdge && canScrollDown ) {
            intensity = (viewportY - edgeBottom) / edgeSize;
            nextScrollY = nextScrollY + ( maxStep * intensity );
        }

        // Sanitize invalid maximums. An invalid scroll offset won't break the
        // subsequent .scrollTo() call; however, it will make it harder to
        // determine if the .scrollTo() method should have been called in the
        // first place.
        nextScrollX = Math.max( 0, Math.min( maxScrollX, nextScrollX ) );
        nextScrollY = Math.max( 0, Math.min( maxScrollY, nextScrollY ) );

        if (
            ( nextScrollX !== currentScrollX ) ||
            ( nextScrollY !== currentScrollY )
        ) {
            handleMoveItem(event);

            if (scrollingContainer) {
                scrollingContainer.scrollTo(nextScrollX, nextScrollY);
            } else {
                window.scrollTo( nextScrollX, nextScrollY );
            }
            return true;
        } else {
            return false;
        }

    }

}


export function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value; //assign the value of ref to the argument
    },[value]); //this code will run when the value of 'value' changes
    return ref.current; //in the end, return the current ref value.
  }


export const recursivelyGetChildren = (db_elements, parentId) => {
    const result = [];
  
    const elementIdsThatPointToParentId = Object.keys(db_elements)
        .filter(key => db_elements[key].parent === parentId)
        .sort((a, b) => db_elements[a].index - db_elements[b].index);
  
    if (elementIdsThatPointToParentId.length > 0) {
        elementIdsThatPointToParentId.map(elementId => {
            if (elementId) {
                const element = db_elements[elementId];
  
                const r = {
                  ...element,
                  id: elementId,
                }
  
                let children = recursivelyGetChildren(db_elements, elementId);
                if (children.length) {
                  r.children = children;
                }
  
                result.push(r)
            }
        })
    } else if (db_elements[parentId] && db_elements[parentId].children){
        // not a reference key
        // console.log(`Nothing points to element ${parentId}, but it has children`);
        return db_elements[parentId].children
    } else {
        // console.log(`Nothing points to element ${parentId}, and it DOES NOT has children`);
    }
    return result;
  };
  
const getChildrenForParentId = (db_elements, parentId) => {
    try {
        return Object.keys(db_elements)
            .filter(elementId => db_elements[elementId].parent === parentId)
            .sort((a, b) => db_elements[a].index - db_elements[b].index);
    } catch (err) {
        console.log(err);
        return [];
    }
  };
  
  // https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
  function array_move(arr, old_index, new_index) {
    if (new_index >= arr.length) {
        var k = new_index - arr.length + 1;
        while (k--) {
            arr.push(undefined);
        }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr; // for testing
  };
  
 export function updateNestedNodes(nodes, id, from, to) {
    const result = { ...nodes };
  
    if (to.droppableId === from.droppableId) {
      // we just need to reorder the siblings
      const siblingIds = getChildrenForParentId(result, to.droppableId);
      array_move(siblingIds, parseInt(from.index), to.index);
  
      // update indieces of siblingIds
      siblingIds.map((siblingId, index) => {
          result[siblingId].index = index;
      });
  
    } else {
      // get indices of current siblings, remove the element, and update indices
      const currentSiblingIds = getChildrenForParentId(result, from.droppableId);
      currentSiblingIds.splice(result[id].index, 1);
      currentSiblingIds.map((siblingId, index) => {
          result[siblingId].index = index;
      });
  
      // get indices of new siblings, insert the element, and update indices
      const newSiblingIds = getChildrenForParentId(result, to.droppableId);
      newSiblingIds.splice(to.index, 0, id);
      newSiblingIds.map((siblingId, index) => {
          result[siblingId].index = index;
          if (siblingId === id) {
              result[siblingId].parent = to.droppableId;
          }
      });
    }
  
    return result;
  }